import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStyletron } from 'baseui';
import type { Theme } from 'baseui/styles';
import { useFitToScreen } from './hooks/use-fit-to-screen';
import { useInterceptors } from './hooks/use-interceptors';
import { useSetInitialScaleOnLoad } from './hooks/use-set-initial-scale-on-load';
import { useStagePointerEvents } from './hooks/use-stage-pointer-events';
import { useStageSettings } from './hooks/use-stage-settings';
import { useZoomAndPan } from './hooks/use-zoom-and-pan';
import { applySettingsToDom } from './utils/apply-settings-to-dom';
import { CoreStageContext } from './context';
import type { CoreStageProviderPropsT } from './types';
const getContainerStyles = (theme: Theme) => ({
  backgroundColor: theme.name.startsWith('light') ? '#F3F3F3' : '#1F1F1F',
  display: ('flex' as const),
  flex: 1,
  overflow: ('hidden' as const),
  position: ('relative' as const)
});
const stageStyles = {
  position: ('relative' as const),
  display: ('inline-block' as const),
  userSelect: ('none' as const)
};
const overlayStyles = {
  position: ('absolute' as const),
  inset: '0',
  pointerEvents: ('none' as const)
};
export function CoreStageProvider({
  children,
  canvasSize,
  stageSettings: stageSettingsProp,
  setLocalStageSettings,
  isDraggable = true,
  suppressPan = false,
  readOnly = false,
  onMount,
  renderOverlay,
  onZoom,
  onPanBlocked,
  minScale,
  maxScale,
  zoomFactor
}: CoreStageProviderPropsT) {
  const [css, theme] = useStyletron();
  const stageRef = useRef<HTMLDivElement>(null);
  const stageBoundingRectRef = useRef<ClientRect | null | undefined>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const stageEventTargetRef = useRef<EventTarget>(new EventTarget());
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const containerCallbackRef = useCallback((el: HTMLDivElement | null) => {
    containerRef.current = el;
    setContainerEl(el);
  }, []);
  const {
    stageSettingsRef,
    onChangeStageSettings,
    onCommitStageSettings
  } = useStageSettings({
    stageSettings: stageSettingsProp,
    setStageSettings: setLocalStageSettings
  });
  const getStageSettings = useCallback(() => stageSettingsRef.current, [stageSettingsRef]);
  const changeAndApplyToDom = useCallback((value: Parameters<typeof onChangeStageSettings>[0]) => {
    onChangeStageSettings(value);
    applySettingsToDom(stageRef.current, stageSettingsRef.current);
  }, [onChangeStageSettings, stageSettingsRef]);
  const commitAndApplyToDom = useCallback((value?: Parameters<typeof onCommitStageSettings>[0]) => {
    onCommitStageSettings(value);
    applySettingsToDom(stageRef.current, stageSettingsRef.current);
  }, [onCommitStageSettings, stageSettingsRef]);
  useEffect(() => {
    onCommitStageSettings(stageSettingsProp);
    applySettingsToDom(stageRef.current, stageSettingsProp);
    stageBoundingRectRef.current = stageRef.current?.getBoundingClientRect();
  }, [stageSettingsProp, onCommitStageSettings]);
  useZoomAndPan({
    containerRef,
    stageRef,
    isDraggable,
    suppressPan,
    onChangeStageSettings: changeAndApplyToDom,
    onCommitStageSettings: commitAndApplyToDom,
    onZoom,
    onPanBlocked,
    minScale,
    maxScale,
    zoomFactor
  });
  const fitToScreen = useFitToScreen({
    stageRef,
    containerRef,
    onCommitStageSettings: commitAndApplyToDom
  });
  useSetInitialScaleOnLoad({
    containerRef,
    stageRef,
    isReady: canvasSize.width > 0 && canvasSize.height > 0,
    onCommitStageSettings: commitAndApplyToDom
  });
  useEffect(() => {
    onMount?.(fitToScreen);
  }, [fitToScreen, onMount]);
  const {
    interceptorStoreRef,
    addInterceptor,
    removeInterceptor
  } = useInterceptors();

  // eslint-disable-next-line react-hooks/refs
  const {
    onStage,
    pointerHandlers
  } = useStagePointerEvents({
    stageRef,
    stageBoundingRectRef,
    // eslint-disable-next-line react-hooks/refs
    stageEventTarget: stageEventTargetRef.current,
    interceptorStoreRef,
    getStageSettings,
    isDraggable,
    readOnly
  });
  const getStageDimension = useCallback((): [number, number] => [canvasSize.width, canvasSize.height], [canvasSize.width, canvasSize.height]);
  const getStageCenter = useCallback((): [number, number] => [canvasSize.width / 2, canvasSize.height / 2], [canvasSize.width, canvasSize.height]);
  const contextValue = useMemo(() => ({
    // eslint-disable-next-line react-hooks/refs
    stage: stageEventTargetRef.current,
    onStage,
    addInterceptor,
    removeInterceptor,
    getStageDimension,
    getStageCenter,
    getStageSettings,
    changeStageSettings: changeAndApplyToDom,
    setStageSettings: commitAndApplyToDom,
    fitToScreen
  }), [onStage, addInterceptor, removeInterceptor, getStageDimension, getStageCenter, getStageSettings, changeAndApplyToDom, commitAndApplyToDom, fitToScreen]);
  return <div ref={containerCallbackRef} className={css(getContainerStyles(theme))} data-testid="core-stage-container">
      {/* eslint-disable-next-line react-hooks/refs */}
      <CoreStageContext.Provider value={contextValue}>
        <div ref={stageRef} className={css(stageStyles)} data-testid="core-stage" style={{
        width: canvasSize.width,
        height: canvasSize.height
      }} {...pointerHandlers}>
          {children}
        </div>
        {renderOverlay && <div className={css(overlayStyles)}>{renderOverlay(containerEl)}</div>}
      </CoreStageContext.Provider>
    </div>;
}